import { getDataTable } from '../../features/visual-analytics/charts/chartDataBindings';
import { getColumnInfo } from '../analyticsDataSlice';
import { TpCanonicalTypeInfo, TpDataContext } from '../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterDefinition,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepMultiLevelCat,
  TpEnSelectionFilterTypes,
} from './interfaces';
import { getEnSelectionFilterStep, removeEnSelectionFilterStep, smartRenderEnFilterItems } from './selectionEnFilters';
import { TpUserEnSelectionAction, TpUserEnSelectionActionMultiLevelCat } from './userSelectionActions';

function getMultiLevelCatEnFilterStepKey(dataTableId: string, colIds: string[]): string {
  return `multiLevelCatFilterStep_${dataTableId}_${colIds.join('_')}`;
}

export function getMultiLevelCatEnFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  dataTableId: string,
  colIds: string[],
): TpEnSelectionFilterStepMultiLevelCat | null {
  const step = getEnSelectionFilterStep(selectionFilter, getMultiLevelCatEnFilterStepKey(dataTableId, colIds));
  if (!step) return null;
  return step as TpEnSelectionFilterStepMultiLevelCat;
}

function handleUserAction(
  state: any,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  userSelectionAction: TpUserEnSelectionAction,
) {
  const userSelectionActionMultiLevelCat = userSelectionAction as TpUserEnSelectionActionMultiLevelCat;
  const key = getMultiLevelCatEnFilterStepKey(
    userSelectionActionMultiLevelCat.dataTableId,
    userSelectionActionMultiLevelCat.colIds,
  );
  const step = getEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
  let stepMultLevelCat: TpEnSelectionFilterStepMultiLevelCat;

  if (!step) {
    // step not yet found - we create it
    stepMultLevelCat = {
      type: TpEnSelectionFilterTypes.MULTILEVELCAT,
      key,
      dataTableId: userSelectionActionMultiLevelCat.dataTableId,
      colIds: userSelectionActionMultiLevelCat.colIds,
      selectedElements: [
        {
          key: userSelectionActionMultiLevelCat.key,
          label: userSelectionActionMultiLevelCat.label,
          selectedItemIds: userSelectionActionMultiLevelCat.selectedItemIds,
        },
      ],
    };
    canonicalTypeInfo.selectionFilter.steps.push(stepMultLevelCat);
  } else {
    // step already found - we modify it
    stepMultLevelCat = step as TpEnSelectionFilterStepMultiLevelCat;
    if (stepMultLevelCat.selectedElements.map((el) => el.key).includes(userSelectionActionMultiLevelCat.key)) {
      // element is present -  we remove it
      stepMultLevelCat.selectedElements = stepMultLevelCat.selectedElements.filter(
        (elem) => elem.key != userSelectionActionMultiLevelCat.key,
      );
      if (stepMultLevelCat.selectedElements.length == 0) {
        // since no more items are left, we remove the entire filter step
        removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
      }
    } else {
      // element is not yet present - we add it
      stepMultLevelCat.selectedElements.push({
        key: userSelectionActionMultiLevelCat.key,
        label: userSelectionActionMultiLevelCat.label,
        selectedItemIds: userSelectionActionMultiLevelCat.selectedItemIds,
      });
    }
  }
}

function performStep(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
): any[] {
  const stepMultiLevelCat = step as TpEnSelectionFilterStepMultiLevelCat;
  const dataTable = getDataTable(dataContext, stepMultiLevelCat.dataTableId);

  const selectedItemIds = [];
  const selectedItemIdMap: { [id: string]: boolean } = {};

  for (const element of stepMultiLevelCat.selectedElements) {
    for (const itemId of element.selectedItemIds)
      if (!selectedItemIdMap[itemId]) {
        selectedItemIdMap[itemId] = true;
        selectedItemIds.push(itemId);
      }
  }
  return selectedItemIds;
}

function renderContent(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
  forceString?: boolean,
) {
  const stepMultiLevelCat = step as TpEnSelectionFilterStepMultiLevelCat;
  const dataTable = getDataTable(dataContext, stepMultiLevelCat.dataTableId);
  const colNames = stepMultiLevelCat.colIds.map((colId) => getColumnInfo(dataTable, colId).name).join(', ');
  const valuesList = stepMultiLevelCat.selectedElements.map((elem) => elem.label);

  if (forceString) return `${colNames}=${smartRenderEnFilterItems(valuesList, true)}`;

  return (
    <span>
      <b>{colNames}</b> = {smartRenderEnFilterItems(valuesList)}
    </span>
  );
}

export const defEnSelectionFilterMultiLevelCat: TpEnSelectionFilterDefinition = {
  handleUserAction,
  performStep,
  renderContent,
};
